"use client"

import { useState } from "react"
import { Link } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { 
  Play, 
  Code, 
  Eye, 
  Zap, 
  Sparkles, 
  ArrowRight, 
  Pause,
  Volume2,
  Maximize,
  CheckCircle2,
  Clock,
  TrendingUp
} from "lucide-react"

interface DemoStep {
  number: number
  title: string
  description: string
  icon: React.ComponentType<{ className?: string }>
  time: string
}

const demoSteps: DemoStep[] = [
  {
    number: 1,
    title: "Describe Your Event",
    description: "Simply tell our AI what event you want to create using natural language. No technical expertise required.",
    icon: Code,
    time: "10s"
  },
  {
    number: 2,
    title: "AI Generates Platform",
    description: "Our advanced AI analyzes your requirements and generates a complete event platform in seconds.",
    icon: Zap,
    time: "15s"
  },
  {
    number: 3,
    title: "Customize & Launch",
    description: "Fine-tune your event with our visual editor, then launch to the world with a single click.",
    icon: Eye,
    time: "5s"
  }
]

const demoStats = [
  { label: "Platform Quality", value: "A+", icon: TrendingUp },
  { label: "Generation Speed", value: "<30s", icon: Clock },
  { label: "Success Rate", value: "99.2%", icon: CheckCircle2 },
  { label: "Templates", value: "50+", icon: Sparkles }
]

export function DemoSection() {
  const [isPlaying, setIsPlaying] = useState(false)
  const [hoveredStep, setHoveredStep] = useState<number | null>(null)

  const handlePlayVideo = () => {
    setIsPlaying(!isPlaying)
    // In production, this would trigger actual video playback
  }

  return (
    <section id="demo" className="relative py-24 overflow-hidden bg-background">
      {/* Animated Background */}
      <div className="absolute inset-0 opacity-30 dark:opacity-20">
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(90deg, rgba(var(--theme-primary), 0.02) 1px, transparent 1px),
              linear-gradient(rgba(var(--theme-primary), 0.02) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px'
          }}
        />
      </div>

      {/* Floating Gradient Orbs */}
      <div className="absolute top-20 left-1/4 w-96 h-96 rounded-full opacity-10 blur-3xl animate-pulse"
        style={{
          background: `radial-gradient(circle, rgb(var(--theme-primary)) 0%, transparent 70%)`,
          animationDuration: '8s'
        }}
      />
      <div className="absolute bottom-20 right-1/4 w-96 h-96 rounded-full opacity-10 blur-3xl animate-pulse"
        style={{
          background: `radial-gradient(circle, rgb(var(--theme-secondary)) 0%, transparent 70%)`,
          animationDuration: '6s',
          animationDelay: '2s'
        }}
      />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center animate-in fade-in slide-in-from-bottom duration-700">
          <Badge 
            variant="secondary" 
            className="mb-6 px-4 py-2 text-sm glass-card border"
            style={{
              borderColor: `rgba(var(--theme-primary), 0.3)`,
              background: `rgba(var(--theme-primary), 0.05)`
            }}
          >
            <Play className="mr-2 h-4 w-4" style={{ color: `rgb(var(--theme-primary))` }} />
            Live Demo
          </Badge>
          
          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-5xl mb-6">
            See Swaraah AI in{" "}
            <span 
              className="relative inline-block"
              style={{
                background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
              Action
              <svg
                className="absolute -bottom-2 left-0 w-full"
                height="8"
                viewBox="0 0 200 8"
                fill="none"
              >
                <path
                  d="M1 5.5C50 2.5 150 2.5 199 5.5"
                  stroke={`rgb(var(--theme-accent))`}
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </svg>
            </span>
          </h2>
          
          <p className="mx-auto mt-6 max-w-3xl text-lg text-muted-foreground leading-relaxed">
            Watch how our AI transforms your ideas into beautiful, functional event platforms in just minutes.
          </p>
        </div>

        {/* Interactive Video/Preview */}
        <div className="mt-20 animate-in fade-in slide-in-from-bottom duration-700 delay-200">
          <div className="relative mx-auto max-w-5xl">
            {/* Glow Effect */}
            <div 
              className="absolute -inset-4 rounded-3xl opacity-20 blur-2xl"
              style={{
                background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`
              }}
            />
            
            <Card className="relative glass-card border backdrop-blur-xl shadow-2xl overflow-hidden">
              <CardContent className="p-0">
                <div className="aspect-video relative overflow-hidden group">
                  {/* Demo Background with Animated Gradient */}
                  <div 
                    className="absolute inset-0 transition-all duration-700"
                    style={{
                      background: isPlaying 
                        ? `linear-gradient(135deg, rgba(var(--theme-primary), 0.1), rgba(var(--theme-secondary), 0.1))`
                        : `linear-gradient(135deg, rgba(var(--theme-primary), 0.05), rgba(var(--theme-secondary), 0.05))`
                    }}
                  />

                  {/* Animated Pattern Overlay */}
                  <div className="absolute inset-0 opacity-10">
                    <div 
                      className="h-full w-full animate-pulse"
                      style={{
                        backgroundImage: `radial-gradient(circle at 25% 25%, rgb(var(--theme-primary)) 0%, transparent 50%), 
                                        radial-gradient(circle at 75% 75%, rgb(var(--theme-secondary)) 0%, transparent 50%)`
                      }}
                    />
                  </div>

                  {/* Play/Pause Button */}
                  <div className="absolute inset-0 flex items-center justify-center z-10">
                    <Button 
                      size="lg" 
                      onClick={handlePlayVideo}
                      className="group h-24 w-24 rounded-full shadow-2xl transition-all duration-500 hover:scale-110 relative overflow-hidden"
                      style={{
                        background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`
                      }}
                    >
                      {/* Pulse Ring */}
                      <div 
                        className="absolute inset-0 rounded-full animate-ping"
                        style={{
                          background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`,
                          opacity: 0.2
                        }}
                      />
                      
                      {isPlaying ? (
                        <Pause className="h-10 w-10 text-white relative z-10" />
                      ) : (
                        <Play className="h-10 w-10 text-white fill-white relative z-10 ml-1" />
                      )}
                    </Button>
                  </div>

                  {/* Video Controls (bottom bar) */}
                  <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/60 to-transparent backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="flex items-center justify-between text-white">
                      <div className="flex items-center gap-4">
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-white hover:bg-white/20"
                          onClick={handlePlayVideo}
                        >
                          {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                        </Button>
                        <span className="text-sm font-medium">
                          {isPlaying ? "0:15 / 2:30" : "2:30"}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <Button variant="outline" size="sm" className="text-white hover:bg-white/20">
                          <Volume2 className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" className="text-white hover:bg-white/20">
                          <Maximize className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="mt-3 h-1 bg-white/20 rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all duration-300"
                        style={{
                          width: isPlaying ? '25%' : '0%',
                          background: `linear-gradient(90deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`
                        }}
                      />
                    </div>
                  </div>

                  {/* Live Stats Overlay */}
                  <div className="absolute top-6 left-6 right-6">
                    <div className="flex items-center justify-between">
                      <Badge className="glass-card border backdrop-blur-md px-3 py-1.5">
                        <div className="flex items-center gap-2">
                          <div className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                          <span className="text-xs font-semibold">LIVE DEMO</span>
                        </div>
                      </Badge>
                      
                      <Badge className="glass-card border backdrop-blur-md px-3 py-1.5">
                        <Sparkles className="h-3 w-3 mr-1.5" style={{ color: `rgb(var(--theme-primary))` }} />
                        <span className="text-xs font-semibold">2.3M+ Events Created</span>
                      </Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Demo Steps with Connector Lines */}
        <div className="mt-24 animate-in fade-in slide-in-from-bottom duration-700 delay-400">
          <div className="grid gap-8 md:grid-cols-3 relative">
            {/* Connector Lines (desktop only) */}
            <div className="hidden md:block absolute top-20 left-0 right-0 h-px">
              <div 
                className="h-full w-2/3 mx-auto"
                style={{
                  background: `linear-gradient(90deg, transparent, rgb(var(--theme-primary)), rgb(var(--theme-secondary)), transparent)`
                }}
              />
            </div>

            {demoSteps.map((step) => {
              const IconComponent = step.icon
              const isHovered = hoveredStep === step.number
              
              return (
                <div 
                  key={step.number} 
                  className="relative"
                  onMouseEnter={() => setHoveredStep(step.number)}
                  onMouseLeave={() => setHoveredStep(null)}
                >
                  <Card className="relative glass-card border backdrop-blur-sm hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 h-full">
                    <CardContent className="p-8 text-center">
                      {/* Step Number Badge */}
                      <div className="absolute -top-6 left-1/2 transform -translate-x-1/2">
                        <div 
                          className="inline-flex h-12 w-12 items-center justify-center rounded-full text-white font-bold text-lg shadow-lg transition-all duration-500"
                          style={{
                            background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`,
                            transform: isHovered ? 'scale(1.2) rotate(5deg)' : 'scale(1)'
                          }}
                        >
                          {step.number}
                        </div>
                      </div>

                      {/* Icon Container */}
                      <div className="mt-8 mb-6 flex justify-center">
                        <div 
                          className="inline-flex h-16 w-16 items-center justify-center rounded-2xl shadow-lg transition-all duration-500"
                          style={{
                            background: isHovered 
                              ? `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`
                              : `rgba(var(--theme-primary), 0.1)`,
                            transform: isHovered ? 'scale(1.1) rotate(-5deg)' : 'scale(1)'
                          }}
                        >
                          <IconComponent 
                            className="h-8 w-8 transition-colors duration-500" />
                        </div>
                      </div>

                      {/* Title with Time Badge */}
                      <div className="mb-3 flex items-center justify-center gap-3">
                        <h3 className="text-xl font-bold text-foreground">
                          {step.title}
                        </h3>
                        <Badge 
                          variant="secondary" 
                          className="text-xs px-2 py-0.5"
                          style={{
                            background: `rgba(var(--theme-primary), 0.1)`,
                            color: `rgb(var(--theme-primary))`
                          }}
                        >
                          {step.time}
                        </Badge>
                      </div>

                      <p className="text-muted-foreground leading-relaxed">
                        {step.description}
                      </p>

                      {/* Progress Indicator */}
                      <div className="mt-6 h-1.5 bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all duration-700"
                          style={{
                            width: isHovered ? '100%' : '0%',
                            background: `linear-gradient(90deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`
                          }}
                        />
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )
            })}
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-20 text-center animate-in fade-in slide-in-from-bottom duration-700 delay-500">
          <Link to="/signup">
            <Button 
              size="lg" 
              className="group text-white shadow-2xl hover:shadow-3xl transition-all duration-300 text-lg px-10 py-7 rounded-xl hover:scale-105"
              style={{
                background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`
              }}
            >
              <Sparkles className="mr-2 h-5 w-5 group-hover:rotate-12 transition-transform" />
              Try It Now - Free
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
          <p className="mt-5 text-sm text-muted-foreground">
            No credit card required • Create your first event in 60 seconds
          </p>
        </div>

        {/* Interactive Stats Grid */}
        <div className="mt-20 grid grid-cols-2 gap-6 md:grid-cols-4 animate-in fade-in slide-in-from-bottom duration-700 delay-600">
          {demoStats.map((stat, index) => {
            const IconComponent = stat.icon
            return (
              <Card key={index} className="group glass-card border backdrop-blur-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-1 cursor-pointer">
                <CardContent className="p-6 text-center">
                  <div className="flex justify-center mb-3">
                    <div 
                      className="p-2 rounded-lg transition-all duration-300 group-hover:scale-110"
                      style={{
                        background: `rgba(var(--theme-primary), 0.1)`
                      }}
                    >
                      <IconComponent 
                        className="h-5 w-5"
                        style={{ color: `rgb(var(--theme-primary))` }}
                      />
                    </div>
                  </div>
                  <div 
                    className="text-3xl font-bold mb-2 transition-all duration-300 group-hover:scale-110"
                    style={{
                      background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text'
                    }}
                  >
                    {stat.value}
                  </div>
                  <div className="text-sm text-muted-foreground font-medium">
                    {stat.label}
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      </div>
    </section>
  )
}
